// ConfirmPH: UI translations.
//
// Scope: English (default) + Tagalog/Filipino only.
//
// Earlier iterations included 9 other Philippine languages but the
// translations were uneven in quality. We've removed them and kept the
// infrastructure clean so a native-speaker reviewer can add languages back
// one at a time (just append a new dictionary to TRANSLATIONS and a new
// row to LANGUAGES: no other code changes needed).
//
// The Tagalog dictionary below was re-read line-by-line in Feb 2026 to
// fix grammar/awkwardness issues. Recent polish pass:
//   * "doble-bookingan" -> "dobleng booking" (cleaner noun form)
//   * "Tingnan ngayon" -> "Suriin ngayon" (checklist verb)
//   * "Mga sabay at babala sa pagitan ng booking" -> "Magkakapatong na booking at mga babala"
//   * "Sa inyo na 'yon" -> "kayo ang bahala" (less slangy)
//   * "Mabuti 'yan" -> "Mabuti naman" (warmer)
//   * "automatic na nangangahulugang" -> "agad nangangahulugang"
//   * "Apektado ang booking" -> "Maaaring apektado ang booking" (softer, matches English)
//   * "pending na kumpirmasyon" -> "nakabinbing kumpirmasyon" (proper Tagalog)
//   * "hindi magagamit" -> "hindi pansamantalang magagamit" (clearer)
//   * Various article/phrasing tightening across find/empty/settings copy.

export const LANGUAGES = [
  { code: "en",  name: "English",            endonym: "English"   },
  { code: "tl",  name: "Filipino",           endonym: "Filipino"  },
];

export const DEFAULT_LANG = "en";
export const STORAGE_KEY = "confirmph_lang_v1";

// ---------------------------------------------------------------------------
// English: source of truth. Every other language inherits any missing key.

const en = {
  "lang.picker.title": "Language",
  "lang.picker.beta": "Found a translation issue? Tell us via Contact.",

  // Navigation
  "nav.group.daily": "Daily",
  "nav.group.manage": "Manage",
  "nav.group.business": "Business",
  "nav.administration": "Finances & Records",
  "nav.dashboard": "Today",
  "nav.bookings": "Bookings",
  "nav.vehicles": "Vehicles",
  "nav.customer_requests": "Booking Change Requests",
  "nav.problems": "Issues",
  "nav.followups": "Tasks",
  "nav.employees": "Employees",
  "nav.drivers": "Drivers",
  "nav.billing": "Plans & Billing",
  "nav.messages": "Messages Sent",
  "nav.help": "Help & Support",
  "nav.settings": "Settings",
  "nav.logout": "Log out",
  "nav.fleet_calendar": "Fleet Calendar",
  "nav.booking_intake": "Booking Intake",
  "nav.fleet_performance": "Fleet Performance",
  "nav.customers": "Customers",
  "nav.activity": "Activity",
  "nav.inquiries": "New Inquiries",
  "nav.reviews": "Reviews",
  "nav.daily_checklists": "Daily Checklists",
  "nav.staff": "Staff on Duty",

  // Common buttons
  "btn.add_booking": "Add Booking",
  "btn.new_booking": "New booking",
  "btn.save": "Save",
  "btn.cancel": "Cancel",
  "btn.confirm": "Confirm",
  "btn.submit": "Submit",
  "btn.search": "Search",
  "btn.view_all": "View all",
  "btn.login": "Login",
  "btn.register": "Register",
  "btn.contact_us": "Contact us",
  "btn.back_to_home": "Back to home",

  // Public header
  "public.nav.find_booking": "Find Booking",
  "public.nav.request_verification": "Request Confirmation",
  "public.nav.faq": "FAQ",
  "public.nav.for_businesses": "For Businesses",
  "public.nav.for_renters": "For Renters",
  "public.nav.login": "Login",

  // Business homepage (/businesses): rebuilt 2026-08-27. Central angle:
  // don't sell ConfirmPH as replacing the owner's way of doing business -
  // sell it as the missing layer behind it. "Messenger gets the
  // conversation. ConfirmPH keeps the rental clear." Main language stays
  // plain professional English with a few natural local recognition
  // moments in quotes ("Available pa ba?" etc.): see biz.recognition.*
  // below and the matching Filipino block for the fuller Taglish version.
  "biz.hero.badge": "Built for Philippine rental businesses",
  "biz.hero.title.a": "Your customers can book on Messenger.",
  "biz.hero.title.b": "Every rental still needs one clear record.",
  "biz.hero.body": "Customers can keep reaching you through Messenger, Facebook, WhatsApp, Viber, phone or walk-in.",
  "biz.hero.lead": "Once they say yes, ConfirmPH gives the rental one shared record for the customer, vehicle, dates, price, confirmation, vehicle status and what needs attention next.",
  "biz.hero.recognition": "No more “akala ko na-record na” or “akala ko ikaw ang nag-handle.”",
  "biz.cta.try5": "Try it with my next 5 bookings",
  "biz.hero.cta.secondary": "See a real rental in ConfirmPH",
  "biz.hero.microcopy": "30 days free · No card required · Keep your existing booking channels",
  "biz.hero.demo": "Try the staff dashboard demo",

  // Hero visual: the chat vs. the shared record
  "biz.hero.visual.chat.customer": "Hi sir available Vios June 12-15?",
  "biz.hero.visual.chat.staff1": "Yes po available 👍",
  "biz.hero.visual.chat.staff2": "Boss booked na pala Vios June 13.",
  "biz.hero.visual.chat.question": "Which one is correct?",
  "biz.hero.visual.record.badge": "Customer confirmed",
  "biz.hero.visual.record.vehicle": "Toyota Vios",
  "biz.hero.visual.record.dates": "June 12 to 15",
  "biz.hero.visual.record.customer.label": "Customer",
  "biz.hero.visual.record.customer.value": "Anna Reyes",
  "biz.hero.visual.record.pickup.label": "Pickup",
  "biz.hero.visual.record.pickup.value": "Dumaguete Airport",
  "biz.hero.visual.record.price.label": "Agreed price",
  "biz.hero.visual.record.price.value": "₱6,500",
  "biz.hero.visual.record.deposit.label": "Deposit",
  "biz.hero.visual.record.deposit.value": "Recorded",
  "biz.hero.visual.record.status.label": "Vehicle status",
  "biz.hero.visual.record.status.value": "Booked",
  "biz.hero.visual.payoff": "One booking · One vehicle · One history",

  // Section: "Parang familiar?": self-recognition questions
  "biz.recognition.eyebrow": "Parang familiar?",
  "biz.recognition.title": "How many of these questions does your team ask every week?",
  "biz.recognition.q1": "“Available pa ba ang Vios bukas?”",
  "biz.recognition.q2": "“Confirmed na ba si customer?”",
  "biz.recognition.q3": "“Sino tumanggap ng advance? Nasaan ang security deposit?”",
  "biz.recognition.q4": "“Dati na ba ’yang gasgas?”",
  "biz.recognition.q5": "“Sino nag-change ng return time?”",
  "biz.recognition.q6": "“Nasabihan na ba yung next customer?”",
  "biz.recognition.payoff": "If your team has to search chats or ask each other, the rental isn't really in one place yet.",

  // Section: "Keep what already works": don't replace Messenger
  "biz.keep.eyebrow": "Keep what already works",
  "biz.keep.title.a": "Don't replace Messenger.",
  "biz.keep.title.b": "Give the rental somewhere proper to live.",
  "biz.keep.channels": "Messenger · Facebook · WhatsApp · Viber · Phone · Walk-in",
  "biz.keep.talk": "Customer talks to you normally",
  "biz.keep.record": "Booking · Vehicle · Customer · Confirmation · Staff · History",
  "biz.keep.note": "Keep the personal service. Add one clear record behind it.",
  "biz.keep.payoff.a": "Don't replace the front door.",
  "biz.keep.payoff.b": "Fix what happens after the customer says yes.",

  // Section: "When small mistake isn't small anymore"
  "biz.consequence.eyebrow": "When “small mistake lang” isn't small anymore",
  "biz.consequence.title": "The problem becomes expensive when the customer is already standing there.",
  "biz.consequence.1.t": "Two customers. One vehicle.",
  "biz.consequence.1.d": "Somebody needs a replacement. Somebody needs an explanation.",
  "biz.consequence.2.t": "The booking was forgotten.",
  "biz.consequence.2.d": "The customer built their day around a rental your team cannot find.",
  "biz.consequence.3.t": "The vehicle has a problem.",
  "biz.consequence.3.d": "The next customer learns about it too late.",
  "biz.consequence.4.t": "Nobody has the same version.",
  "biz.consequence.4.d": "Price, dates, pickup or vehicle condition becomes an argument.",
  "biz.consequence.payoff.a": "The problem isn't bad staff.",
  "biz.consequence.payoff.b": "It's important information living in different places.",

  // Section: "One rental. One record."
  "biz.standard.eyebrow": "One rental. One record.",
  "biz.standard.title": "Every rental should answer five questions immediately.",
  "biz.standard.q1": "Who is renting?",
  "biz.standard.q2": "Which vehicle?",
  "biz.standard.q3": "When?",
  "biz.standard.q4": "What was agreed: price, advance, security deposit, and who drives?",
  "biz.standard.q5": "What needs attention next?",
  "biz.standard.note": "If the answer means searching Messenger, checking another sheet and asking somebody else, the rental is still spread across the business.",
  "biz.standard.rule": "If it's a rental, record it.",
  "biz.standard.rule.before": "“Sino nakausap niya?”",
  "biz.standard.rule.after": "“Nasa ConfirmPH na?”",

  // Section: "Start the day clear"
  "biz.today.eyebrow": "Start the day clear",
  "biz.today.title": "Know what's booked. Know what's available. Know what needs attention.",
  "biz.today.pickups": "Today's pickups",
  "biz.today.returns": "Today's returns",
  "biz.today.waiting": "Waiting for confirmation",
  "biz.today.issues": "Vehicle issues",
  "biz.today.followups": "Follow-ups",
  "biz.today.cta": "Open ConfirmPH. Know what needs attention.",
  "biz.today.benefit": "Your business should know what happened even when you were not the person who answered the customer.",
  "biz.today.owner": "The owner doesn't have to be the database.",

  // Section: product walkthrough (component: BusinessProductWalkthrough)
  "biz.proof.eyebrow": "Real product. Demo data.",
  "biz.proof.title": "Watch one rental go from Messenger to return.",
  "biz.proof.mode.normal": "Normal rental",
  "biz.proof.mode.issue": "What if something goes wrong?",
  "biz.proof.cta.normal": "Start the rental walkthrough",
  "biz.proof.cta.issue": "See what happens when something goes wrong",
  "biz.proof.browse_all": "Browse every demo scenario",

  // Section: "Maliit pa kami" objection
  "biz.small.eyebrow": "“Maliit pa kami.”",
  "biz.small.title": "You don't need a big fleet to need clear bookings.",
  "biz.small.body": "Whether you have 3 units or 30, the customer still expects the right vehicle on the right day.",
  "biz.small.body2": "And the easiest time to build a good operating habit is before more vehicles, customers and staff make the old way harder to control.",
  "biz.small.note.a": "Don't move your whole business.",
  "biz.small.note.b": "Put the next 5 real bookings into ConfirmPH.",
  "biz.small.reassure1": "Keep Messenger",
  "biz.small.reassure2": "Start with booking + confirmation",
  "biz.small.reassure3": "Use more features only when you need them",

  // Section: onboarding reassurance ("not an IT project")
  "biz.simple.eyebrow": "You don't need to be techy",
  "biz.simple.title": "Sorting out bookings shouldn't feel like an IT project.",
  "biz.simple.flow": "Add booking → Choose vehicle → Add customer → Send confirmation → Continue the rental",
  "biz.simple.help.t": "Need a hand?",
  "biz.simple.help.d": "We can help guide your team through initial setup and the first real bookings.",

  // Section: "Customers don't see your system. They feel it."
  "biz.feel.eyebrow": "Customers don't see your system.",
  "biz.feel.title": "They feel it.",
  "biz.feel.1": "They feel it when the booking is clear.",
  "biz.feel.2": "They feel it when two staff members give the same answer.",
  "biz.feel.3": "They feel it when the correct vehicle is ready.",
  "biz.feel.4": "They feel it when a problem is handled before they have to chase you.",
  "biz.feel.5": "They feel it when they don't need to explain the booking again.",
  "biz.feel.payoff": "A small rental business can still feel professionally run.",
  "biz.feel.note": "Keep the relationship. Improve the system behind it.",

  // Section: "Less sisihan. More clarity.": audit trail, not surveillance
  "biz.clarity.eyebrow": "Less sisihan. More clarity.",
  "biz.clarity.less1": "“Sino nag-confirm nito?”",
  "biz.clarity.less2": "“Akala ko ikaw nag-handle.”",
  "biz.clarity.less3": "“Hindi ko nakita yung message.”",
  "biz.clarity.more": "“Nandito ang booking.”",
  "biz.clarity.1": "Booking recorded",
  "biz.clarity.2": "Vehicle recorded",
  "biz.clarity.3": "Customer confirmation recorded",
  "biz.clarity.4": "Changes recorded",
  "biz.clarity.5": "Relevant activity recorded",
  "biz.clarity.note": "Everyone on the team can see the same information.",

  // Section: "Before you compare the price": the money frame
  "biz.money.eyebrow": "Before you compare the price",
  "biz.money.title": "Messenger is free. Mistakes aren't.",
  "biz.money.1": "A forgotten booking",
  "biz.money.2": "A preventable double booking",
  "biz.money.3": "A customer who needs a refund or a replacement vehicle",
  "biz.money.4": "A day spent figuring out what was actually agreed",
  "biz.money.5": "A vehicle problem the next customer finds out about too late",
  "biz.money.note.a": "It's not the Messenger message that's expensive.",
  "biz.money.note.b": "It's the problem, when the business has no shared record.",

  // Section: "Protect the bookings you worked hard to get"
  "biz.protect.eyebrow": "Protect the bookings you worked hard to get",
  "biz.protect.title": "Don't let a booking disappear after you've already won the customer.",
  "biz.protect.1": "You post.",
  "biz.protect.2": "You answer inquiries.",
  "biz.protect.3": "You follow up.",
  "biz.protect.4": "You buy and maintain vehicles.",
  "biz.protect.5": "You build a reputation.",
  "biz.protect.note": "Once the customer says yes, don't let the booking get lost between messages, people and memory.",

  // Section: RUN / TRUST / GROW: compressed feature groups
  "biz.groups.eyebrow": "Everything ConfirmPH does",
  "biz.groups.run.t": "Run the rental",
  "biz.groups.run.d": "Bookings and agreements, fleet, inspections, maintenance, incidents, staff and records, plus Legal Readiness, company drivers and authorized self-drive drivers, Finance & Books, and return and settlement.",
  "biz.groups.trust.t": "Build customer trust",
  "biz.groups.trust.d": "Confirmation links, booking history, public profiles and booking-based reviews.",
  "biz.groups.grow.t": "Grow the business",
  "biz.groups.grow.d": "Discover Rentals, public presence, websites, integrations and advanced tools.",
  "biz.groups.cta": "Explore everything ConfirmPH can do",

  // Final CTA
  "biz.final.title.a": "Your next booking is already coming in.",
  "biz.final.title.b": "Where will it live?",
  "biz.final.q1": "Another Messenger thread?",
  "biz.final.q2": "Another screenshot?",
  "biz.final.q3": "A notebook?",
  "biz.final.q4": "Someone's memory?",
  "biz.final.q5": "Or somewhere your whole team can actually see it?",
  "biz.final.instruction": "Put your next 5 bookings into ConfirmPH.",
  "biz.final.keep1": "Keep Messenger.",
  "biz.final.keep2": "Keep Facebook.",
  "biz.final.keep3": "Keep WhatsApp.",
  "biz.final.keep4": "Keep taking calls and walk-ins.",
  "biz.final.instruction2": "Just give the next five rentals one proper record.",
  "biz.final.cta.primary": "Try ConfirmPH with my next 5 bookings",
  "biz.final.cta.secondary": "See the demo first",
  "biz.final.microcopy": "30 days free · No card required",
  "biz.final.brandline": "Message anywhere. Run the rental in ConfirmPH.",

  // Business navigation (see components/PublicLayout.jsx)
  "biz.nav.product": "Product",
  "biz.nav.how_it_works": "How It Works",
  "biz.nav.plans": "Plans",
  "biz.nav.guides": "Business Guides",
  "biz.nav.help": "Help",

  // Customer-first home (`/`): rebuilt around one behavioral goal: make
  // checking a booking before paying/travelling feel as normal as checking
  // a flight ticket before the airport. See the 2026-08-27 homepage brief.
  "home.customer.badge": "For renters & customers",

  // 1. Hero
  "home.hero.title.a": "Before you pay more or travel to pickup, make sure your booking is",
  "home.hero.title.b": "actually recorded.",
  "home.hero.body1": "You agreed on the vehicle. You confirmed the dates. Maybe you even checked again.",
  "home.hero.body2.pre": "But a message saying ",
  "home.hero.body2.word": "confirmed",
  "home.hero.body2.post": " does not always tell you what the rental business actually recorded.",
  "home.hero.lead": "ConfirmPH gives you a clear record of the vehicle, dates, price and pickup details before you depend on the booking.",
  "home.hero.cta.primary.helper": "See what the rental business recorded.",
  "home.hero.compat": "Booked through Messenger, Facebook, WhatsApp, Viber, phone or walk-in? That's fine.",

  // Shared CTAs reused across several sections of this page.
  "home.cta.check_booking": "Check my booking",
  "home.cta.get_in_writing": "Get my booking in writing",
  "home.cta.discover": "Discover rentals",
  "home.cta.still_looking": "Still looking?",

  // 2. Hero visual: chat vs. structured record
  "home.hero.visual.chat.customer": "Toyota Vios June 12 to 15 available?",
  "home.hero.visual.chat.business": "Yes sir confirmed 👍",
  "home.hero.visual.transition": "But what exactly was confirmed?",
  "home.hero.visual.record.badge": "Booking recorded",
  "home.hero.visual.record.vehicle": "Toyota Vios",
  "home.hero.visual.record.dates": "June 12 to 15",
  "home.hero.visual.record.pickup.label": "Pickup",
  "home.hero.visual.record.pickup.value": "Dumaguete Airport",
  "home.hero.visual.record.price.label": "Price",
  "home.hero.visual.record.price.value": "₱6,000",
  "home.hero.visual.record.deposit.label": "Deposit",
  "home.hero.visual.record.deposit.value": "₱1,500",
  "home.hero.visual.record.ref.label": "Booking reference",
  "home.hero.visual.record.ref.value": "BK-7K3M9P2X",
  "home.hero.visual.record.confirmed": "Customer confirmed",
  "home.hero.visual.payoff.a": "A chat tells you what someone said.",
  "home.hero.visual.payoff.b": "A booking record shows you what was recorded.",

  // 3. The standard: four questions to test a booking against
  "home.standard.eyebrow": "Before you call it confirmed",
  "home.standard.title": "Can you answer these four questions without searching through your messages?",
  "home.standard.q1.t": "Which vehicle?",
  "home.standard.q1.d": "The exact vehicle or clearly agreed category.",
  "home.standard.q2.t": "Which dates?",
  "home.standard.q2.d": "Pickup and return date and time.",
  "home.standard.q3.t": "How much?",
  "home.standard.q3.d": "Rental price, any advance or downpayment, any refundable security deposit, and the remaining rental balance.",
  "home.standard.q4.t": "Where?",
  "home.standard.q4.d": "Exact pickup location and who to contact.",
  "home.standard.payoff": "If one of those is unclear, the booking is not clear yet.",

  // 4. The consequence: why check before you go
  "home.consequence.eyebrow": "Why check before you go",
  "home.consequence.title": "Booking problems get expensive when you discover them too late.",
  "home.consequence.1.t": "Before you pay",
  "home.consequence.1.d": "There is still time to correct the price, advance, security deposit, dates or vehicle.",
  "home.consequence.2.t": "Before you travel",
  "home.consequence.2.d": "There is still time to confirm the pickup point and contact details.",
  "home.consequence.3.t": "At pickup",
  "home.consequence.3.d": "It may already be too late to fix the problem without losing time, money or your original plans.",
  "home.consequence.payoff": "Catch the misunderstanding while there is still time to fix it.",

  // 5. The mechanism: how ConfirmPH works
  "home.flow.eyebrow": "How ConfirmPH works",
  "home.flow.title": "Keep booking normally. Add one clear confirmation step.",
  "home.flow.1.t": "Book however you already book",
  "home.flow.1.d": "Messenger, Facebook, WhatsApp, Viber, phone or in person.",
  "home.flow.2.t": "The business records the rental",
  "home.flow.2.d": "Vehicle, dates, service mode, price, advance and security deposit, pickup and customer details.",
  "home.flow.3.t": "You review the same information",
  "home.flow.3.d": "Confirm the specific booking version presented to you, or ask the business to correct something.",
  "home.flow.payoff": "Both sides can reference the same booking.",
  "home.flow.note": "No new way of contacting the business. No complicated booking process. Just a clearer record of what was agreed.",

  // 6. Product proof: the interactive demo (component: ProductWalkthrough)
  "home.proof.eyebrow": "See it yourself",
  "home.proof.title": "See exactly what the renter sees.",
  "home.proof.subtitle": "Follow a demo rental from booking details and confirmation through pickup and completion.",
  "home.proof.mode.normal": "Normal rental journey",
  "home.proof.mode.issue": "What if something goes wrong?",
  "home.proof.teaser.normal": "Walk through the real renter journey, from finding a rental to leaving a review. Real screens, nothing leaves this page.",
  "home.proof.teaser.issue": "See how ConfirmPH handles a vehicle problem on a confirmed booking, from the warning to your reconfirmation. Real screens, nothing leaves this page.",
  "home.proof.cta.normal": "See a confirmed booking",
  "home.proof.cta.issue": "See how ConfirmPH responds",
  "home.proof.business_prompt": "Running a rental business?",
  "home.proof.business_link": "See how ConfirmPH works for your business",

  // 7. Trust through limitation
  "home.trust.title": "What does \"confirmed\" actually mean?",
  "home.trust.does.t": "ConfirmPH confirms",
  "home.trust.does.d": "That the customer accepted the particular booking version and material terms presented at that time. If a material term changes later, the new version needs its own acceptance; it never inherits the earlier confirmation.",
  "home.trust.doesnot.t": "ConfirmPH does not guarantee",
  "home.trust.doesnot.d": "That circumstances cannot change, that the rental business will perform perfectly, or that ConfirmPH owns or controls the rental business.",
  "home.trust.footer": "ConfirmPH gives both sides a clearer record. The rental business remains responsible for the rental itself.",

  // 8. The habit, before pickup
  "home.habit.eyebrow": "Before pickup",
  "home.habit.title": "Six things you should know without opening Messenger.",
  "home.habit.1": "Which vehicle",
  "home.habit.2": "Exact pickup and return dates",
  "home.habit.3": "Agreed price, any advance, and the security deposit",
  "home.habit.4": "Exact pickup location",
  "home.habit.5": "Who to contact if something changes",
  "home.habit.6": "Who is driving: self-drive with an authorized driver, or a driver from the business",
  "home.habit.mantra": "Before pickup, check ConfirmPH.",

  // 9. Still looking: discovery folded into the same journey
  "home.discover.eyebrow": "Still looking?",
  "home.discover.title": "Start with rental businesses already using ConfirmPH.",
  "home.discover.subtitle": "Browse participating rental businesses, see their listed vehicles and public information, then contact the business.",
  "home.discover.journey": "Discover → Book → Record → Confirm → Readiness → Handover",

  // 10. Final CTA
  "home.final.title.a": "You already checked the messages.",
  "home.final.title.b": "Now check the booking.",
  "home.final.body": "Before you pay more or travel to pickup, make sure the vehicle, dates, price and pickup details are clearly recorded.",
  "home.final.secondary.label": "Booked outside ConfirmPH?",

  "home.business.bridge.eyebrow": "Run a rental business?",
  "home.business.bridge.title": "Stop losing bookings to forgotten messages.",
  "home.business.bridge.subtitle": "ConfirmPH gives small rental businesses in the Philippines a calm dashboard to confirm bookings, catch double-bookings, and follow up on time.",
  "home.business.bridge.cta": "Learn more for businesses",

  // Dashboard workspace
  "dash.title": "Today's workspace",
  "dash.subtitle": "What to do today.",
  "dash.how_it_works": "How ConfirmPH works",
  "dash.attention.title": "What needs my attention?",
  "dash.attention.subtitle": "Top items to handle today",
  "dash.attention.empty": "Nothing urgent right now. Add a booking or close the laptop: your call.",
  "dash.checklist.title": "Today, check",
  "dash.checklist.subtitle": "5-minute checklist",
  "dash.chk.pending": "Confirm new bookings",
  "dash.chk.today": "Recheck today's vehicles",
  "dash.chk.problems": "Resolve vehicle problems",
  "dash.chk.followups": "Handle follow-ups",
  "dash.chk.customer_requests": "Answer customer requests",
  "dash.ws.today_pickups": "Today's Pickups",
  "dash.ws.today_returns": "Today's Returns",
  "dash.ws.needs_confirmation": "Needs Confirmation",
  "dash.ws.problems": "Problems",
  "dash.ws.followups": "Follow-ups Due",
  "dash.ws.customer_requests": "Customer Requests",
  "dash.upcoming.title": "Upcoming bookings",
  "dash.upcoming.subtitle": "Next 7 days",
  "dash.upcoming.empty": "Nothing upcoming this week.",
  "dash.at_risk.title": "At-risk bookings",
  "dash.at_risk.subtitle": "Conflicts and buffer warnings",
  "dash.at_risk.empty": "No conflicts detected. Nice.",

  // Booking status labels
  "status.pending_client_confirmation": "Pending Confirmation",
  "status.client_confirmed": "Customer Confirmed",
  "status.change_requested": "Change Requested",
  "status.at_risk": "At Risk",
  "status.affected_action_needed": "Action Needed",
  "status.cancelled": "Cancelled",
  "status.completed": "Completed",

  // Status tooltips
  "tip.awaiting_staff_approval": "Needs staff review before moving forward.",
  "tip.pending_client_confirmation": "Customer still needs to confirm the booking details.",
  "tip.reconfirmation_required": "Booking details changed after the customer confirmed: they need to reconfirm.",
  "tip.client_confirmed": "Customer checked and confirmed the details.",
  "tip.change_requested": "Customer says something needs correction.",
  "tip.ongoing": "Vehicle has been picked up: rental is in progress.",
  "tip.at_risk": "Something may affect this booking. Check before pickup.",
  "tip.affected_action_needed": "This booking is likely affected by a problem.",
  "tip.completed": "Booking finished.",
  "tip.cancelled": "This booking was cancelled.",
  "tip.no_show": "The customer did not show up for pickup.",

  // Empty states
  "empty.bookings.title": "No bookings yet",
  "empty.bookings.body": "When a customer messages, calls, or walks in, add the booking here so they can confirm it.",
  "empty.vehicles.title": "No vehicles yet",
  "empty.vehicles.body": "Add your vehicles first so bookings can be connected to the right car, motor, van, or SUV.",
  "empty.problems.title": "No active vehicle problems",
  "empty.problems.body": "If a vehicle is damaged, late, or unavailable, log it here so affected customers can be warned.",
  "empty.followups.title": "No follow-ups due",
  "empty.followups.body": "ConfirmPH will show follow-ups here when bookings need attention: pending confirmations, pre-pickup checks, and return prep.",
  "empty.customer_requests.title": "No customer requests yet",
  "empty.customer_requests.body": "These appear when customers ask your business to confirm a booking through ConfirmPH.",

  // Find My Booking
  "find.title": "Find my booking",
  "find.subtitle": "Check whether your booking is recorded in the company's ConfirmPH system.",
  "find.noresult.title": "No booking was found in ConfirmPH.",
  "find.noresult.body": "This does not automatically mean your booking is cancelled. It may mean the business has not entered it yet, or is not using ConfirmPH. Contact them or request confirmation to avoid surprises.",
  "find.cta.request": "Request Booking Confirmation",

  // Settings
  "settings.title": "Settings",
  "settings.language.title": "Language",
  "settings.language.subtitle": "Choose how ConfirmPH talks to you. Affects this device only.",

  // Warning Center (nav)
  "nav.warnings": "Warnings",

  // Warning Center: page
  "warnings.page.title": "Warning Center",
  "warnings.page.subtitle": "Operational risks we've detected across your bookings, vehicles, and incidents. Review each item and take action: nothing is sent to your customers automatically.",
  "warnings.rescan": "Refresh warnings",
  "warnings.loading": "Loading warnings…",
  "warnings.tab.active": "Active",
  "warnings.tab.closed": "Closed",
  "warnings.filter.all_types": "All types",
  "warnings.filter.all_severities": "All severities",
  "warnings.severity.critical": "Critical",
  "warnings.severity.high": "High",
  "warnings.severity.medium": "Medium",
  "warnings.severity.low": "Low",
  "warnings.severity.info": "Info",
  "warnings.empty.active.title": "All clear: no active warnings right now.",
  "warnings.empty.active.body": "We'll flag anything that needs your attention as it happens.",
  "warnings.empty.closed.title": "Nothing to show here yet.",
  "warnings.empty.closed.body": "Closed warnings will appear here.",
  "warnings.toast.scan_complete": "Scan complete: {detected} issue(s) detected, {fixed} auto-resolved.",

  // Warning Center: card
  "warnings.recommended": "Recommended",
  "warnings.detected_at": "Detected",
  "warnings.last_seen": "last seen",
  "warnings.closed_at": "closed",
  "warnings.internal_notes": "Internal notes",
  "warnings.show_notes": "Notes",
  "warnings.hide_notes": "Hide",
  "warnings.more_actions": "More actions",
  "warnings.affected.booking": "Booking",
  "warnings.affected.vehicle": "Vehicle",
  "warnings.affected.incident": "Incident",

  // Warning Center: status labels
  "warnings.status.active": "Active",
  "warnings.status.reviewed": "Reviewed",
  "warnings.status.closed": "Closed",
  "warnings.status.fixed": "Auto-fixed",

  // Warning Center: actions
  "warnings.action.reassign": "Reassign vehicle",
  "warnings.action.notify": "Notify customer",
  "warnings.action.open_booking": "Open booking",
  "warnings.action.open_incident": "Open incident",
  "warnings.action.open_vehicle_documents": "Open vehicle documents",
  "warnings.action.open_vehicle_maintenance": "View maintenance",
  "warnings.action.review": "Mark reviewed",
  "warnings.action.add_note": "Add note",
  "warnings.action.close": "Close",

  // Warning Center: notify / note forms
  "warnings.notify_placeholder": "Custom message (optional: we'll send a safe default if left blank)",
  "warnings.notify.disclaimer": "We never auto-notify customers: this only sends when you click Send.",
  "warnings.send_notification": "Send notification",
  "warnings.note_placeholder": "Internal note (only visible to your team)",
  "warnings.save_note": "Save note",
  "warnings.toast.reviewed": "Marked as reviewed",
  "warnings.toast.closed": "Warning closed",
  "warnings.toast.note_empty": "Note cannot be empty.",
  "warnings.toast.note_added": "Note added",
  "warnings.toast.customer_notified": "Customer notified",

  // Warning Center: close (structured reasons)
  "warnings.close.title": "Why are you closing this warning?",
  "warnings.close.reason.resolved": "Resolved",
  "warnings.close.reason.not_an_issue": "Not an issue",
  "warnings.close.reason.accepted_risk": "Accepted risk",
  "warnings.close.reason.duplicate": "Duplicate",
  "warnings.close.reason.other": "Other",
  "warnings.close.note_required_placeholder": "Briefly explain why (required)",
  "warnings.close.note_optional_placeholder": "Add a note (optional)",
  "warnings.close.note_required": "A short note is required for this reason.",
  "warnings.close.submit": "Close warning",

  // Warning Center: banner
  "warnings.banner.count_one": "1 active warning",
  "warnings.banner.count_many": "{count} active warnings",
  "warnings.banner.minimize": "Minimize",
  "warnings.banner.expand": "Expand",

  // Warning Center: types (chip label)
  "warnings.type.booking_overlap": "Overlapping bookings",
  "warnings.type.buffer_violation": "Short turnaround",
  "warnings.type.vehicle_unavailable": "Vehicle unavailable",
  "warnings.type.incident_impact": "Vehicle issue",
  "warnings.type.missing_fields": "Missing details",
  "warnings.type.pending_too_long": "Awaiting confirmation",
  "warnings.type.return_overdue": "Return overdue",
  "warnings.type.deposit_unresolved": "Deposit unresolved",
  "warnings.type.missing_return_inspection": "Inspection missing",
  "warnings.type.inspection_damage": "Possible damage",
  "warnings.type.vehicle_document_expiry": "Document expiring",
  "warnings.type.location_risk": "Location / distance risk",
  "warnings.type.maintenance_due": "Maintenance due",

  // Warning Center: "why it matters"
  "warnings.why.booking_overlap": "Two customers are booked on the same vehicle at overlapping times: one of them won't have a car unless this is fixed before pickup.",
  "warnings.why.buffer_violation": "There may not be enough time to clean, inspect, and prep the vehicle before the next customer arrives.",
  "warnings.why.vehicle_unavailable": "The vehicle assigned to this booking isn't ready to rent: the customer may show up to no vehicle.",
  "warnings.why.incident_impact": "An open incident on this vehicle could affect the customer's pickup or the vehicle's condition.",
  "warnings.why.missing_fields": "Without full contact or scheduling details, staff may not be able to reach the customer or prep the booking correctly.",
  "warnings.why.pending_too_long": "The customer hasn't confirmed after 24+ hours: the vehicle may be held for a booking that never happens.",
  "warnings.why.return_overdue": "The vehicle hasn't been returned on time, which can delay or block the next booking on this vehicle.",
  "warnings.why.deposit_unresolved": "The rental ended over a week ago and the deposit hasn't been refunded or withheld: this can turn into a dispute later.",
  "warnings.why.missing_return_inspection": "Without a return inspection on file, damage or deposit disputes are hard to resolve later.",
  "warnings.why.inspection_damage": "Unreviewed damage findings can hold up the deposit decision and any repair billing.",
  "warnings.why.vehicle_document_expiry": "An expired or soon-to-expire document can make the vehicle non-compliant to rent out.",
  "warnings.why.location_risk": "Pickup/return distance may affect this booking: confirm logistics with the customer.",
  "warnings.why.maintenance_due": "Skipping or delaying scheduled service can lead to breakdowns, safety issues, or bigger repair bills later.",
};

// ---------------------------------------------------------------------------
// Tagalog / Filipino: reviewed Feb 2026.

const tl = {
  "lang.picker.title": "Wika",
  "lang.picker.beta": "May nakitang maling salin? Sabihan kami sa Contact.",

  // Navigation
  "nav.group.daily": "Araw-araw",
  "nav.group.manage": "Pamamahala",
  "nav.group.business": "Negosyo",
  "nav.administration": "Pananalapi at Rekord",
  "nav.dashboard": "Ngayong Araw",
  "nav.bookings": "Mga Booking",
  "nav.vehicles": "Mga Sasakyan",
  "nav.customer_requests": "Mga Hiling na Pagbabago ng Booking",
  "nav.problems": "Mga Isyu",
  "nav.followups": "Mga Gawain",
  "nav.employees": "Mga Empleyado",
  "nav.drivers": "Mga Drayber",
  "nav.billing": "Plano at Bayarin",
  "nav.messages": "Mga Mensaheng Naipadala",
  "nav.help": "Tulong at Suporta",
  "nav.settings": "Mga Setting",
  "nav.logout": "Mag-log out",
  "nav.fleet_calendar": "Kalendaryo ng Fleet",
  "nav.booking_intake": "Pagtanggap ng Booking",
  "nav.fleet_performance": "Performance ng Fleet",
  "nav.customers": "Mga Kustomer",
  "nav.activity": "Aktibidad",
  "nav.inquiries": "Mga Bagong Tanong",
  "nav.reviews": "Mga Review",
  "nav.daily_checklists": "Pang-araw-araw na Checklist",
  "nav.staff": "Staff na Naka-duty",

  // Common buttons
  "btn.add_booking": "Magdagdag ng Booking",
  "btn.new_booking": "Bagong booking",
  "btn.save": "I-save",
  "btn.cancel": "Kanselahin",
  "btn.confirm": "Kumpirmahin",
  "btn.submit": "Ipadala",
  "btn.search": "Maghanap",
  "btn.view_all": "Tingnan ang lahat",
  "btn.login": "Mag-login",
  "btn.register": "Mag-rehistro",
  "btn.contact_us": "Makipag-ugnayan sa amin",
  "btn.back_to_home": "Bumalik sa Home",

  // Public header
  "public.nav.find_booking": "Hanapin ang Booking",
  "public.nav.request_verification": "Humiling ng Kumpirmasyon",
  "public.nav.faq": "FAQ",
  "public.nav.for_businesses": "Para sa Negosyo",
  "public.nav.for_renters": "Para sa Umuupa",
  "public.nav.login": "Mag-login",

  // Business homepage (/businesses): professional Filipino with normal
  // business Taglish. Terms like booking, rental, unit, customer, staff,
  // deposit, pickup, return and confirmed stay in English on purpose -
  // that's how Philippine rental operators actually speak. See the
  // matching English block above for the section-by-section brief.
  "biz.hero.badge": "Ginawa para sa mga rental business sa Pilipinas",
  "biz.hero.title.a": "Puwedeng mag-book ang customers mo sa Messenger.",
  "biz.hero.title.b": "Pero bawat rental dapat may isang malinaw na record.",
  "biz.hero.body": "Puwede pa ring mag-inquire at mag-book ang customers mo sa Messenger, Facebook, WhatsApp, Viber, phone o walk-in.",
  "biz.hero.lead": "Kapag nag-yes na ang customer, binibigyan ng ConfirmPH ang rental ng isang shared record para sa customer, unit, dates, presyo, confirmation, vehicle status at kung ano ang kailangang asikasuhin next.",
  "biz.hero.recognition": "Wala nang “Akala ko na-record na.” o “Akala ko ikaw ang nag-handle.”",
  "biz.cta.try5": "Subukan sa next 5 bookings ko",
  "biz.hero.cta.secondary": "Tingnan ang isang actual rental sa ConfirmPH",
  "biz.hero.microcopy": "30 days free · No card required · Tuloy lang ang Messenger, Facebook, WhatsApp, phone at walk-ins",
  "biz.hero.demo": "Subukan ang staff dashboard demo",

  // Hero visual: the chat vs. the shared record
  "biz.hero.visual.chat.customer": "Hi sir, available Vios June 12–15?",
  "biz.hero.visual.chat.staff1": "Yes po, available 👍",
  "biz.hero.visual.chat.staff2": "Boss, booked na pala ang Vios sa June 13.",
  "biz.hero.visual.chat.question": "Alin ang tama?",
  "biz.hero.visual.record.badge": "Customer confirmed",
  "biz.hero.visual.record.vehicle": "Toyota Vios",
  "biz.hero.visual.record.dates": "June 12–15",
  "biz.hero.visual.record.customer.label": "Customer",
  "biz.hero.visual.record.customer.value": "Anna Reyes",
  "biz.hero.visual.record.pickup.label": "Pickup",
  "biz.hero.visual.record.pickup.value": "Dumaguete Airport",
  "biz.hero.visual.record.price.label": "Agreed price",
  "biz.hero.visual.record.price.value": "₱6,500",
  "biz.hero.visual.record.deposit.label": "Deposit",
  "biz.hero.visual.record.deposit.value": "Recorded",
  "biz.hero.visual.record.status.label": "Vehicle status",
  "biz.hero.visual.record.status.value": "Booked",
  "biz.hero.visual.payoff": "Isang booking. Isang unit. Isang history.",

  // Section: "Parang familiar?"
  "biz.recognition.eyebrow": "Parang familiar?",
  "biz.recognition.title": "Ilan dito ang tinatanong ng team mo bawat linggo?",
  "biz.recognition.q1": "“Available pa ba ang Vios bukas?”",
  "biz.recognition.q2": "“Confirmed na ba si customer?”",
  "biz.recognition.q3": "“Sino tumanggap ng advance? Nasaan ang security deposit?”",
  "biz.recognition.q4": "“Dati na ba ’yang gasgas?”",
  "biz.recognition.q5": "“Sino nag-change ng return time?”",
  "biz.recognition.q6": "“Nasabihan na ba yung next customer?”",
  "biz.recognition.payoff": "Kung kailangan pang maghanap sa chats o magtanungan ang staff, hindi pa talaga nasa iisang lugar ang rental.",

  // Section: "Keep what already works"
  "biz.keep.eyebrow": "Keep what already works",
  "biz.keep.title.a": "Huwag alisin ang Messenger.",
  "biz.keep.title.b": "Bigyan lang ng tamang lugar ang rental record.",
  "biz.keep.channels": "Messenger · Facebook · WhatsApp · Viber · Phone · Walk-in",
  "biz.keep.talk": "Normal pa rin ang pakikipag-usap sa customer.",
  "biz.keep.record": "Booking · Unit · Customer · Confirmation · Staff · History",
  "biz.keep.note": "Panatilihin ang personal na serbisyo. Dagdagan lang ng isang malinaw na record sa likod nito.",
  "biz.keep.payoff.a": "Huwag palitan kung saan pumapasok ang customer.",
  "biz.keep.payoff.b": "Ayusin kung ano ang nangyayari pagkatapos niyang mag-yes.",

  // Section: "Kapag ang 'small mistake lang' ay hindi na small"
  "biz.consequence.eyebrow": "Kapag ang “small mistake lang” ay hindi na small",
  "biz.consequence.title": "Nagiging mahal ang problema kapag nandiyan na ang customer.",
  "biz.consequence.1.t": "Dalawang customer. Isang unit.",
  "biz.consequence.1.d": "May isang madidismaya. May kailangang hanapan ng replacement. May kailangang magpaliwanag.",
  "biz.consequence.2.t": "Nakalimutan ang booking.",
  "biz.consequence.2.d": "Nagplano ang customer base sa rental na hindi mahanap ng team mo.",
  "biz.consequence.3.t": "May problema ang unit.",
  "biz.consequence.3.d": "Late na nalalaman ng next customer na maaapektuhan pala ang booking niya.",
  "biz.consequence.4.t": "Magkakaiba ang naaalala ng bawat isa.",
  "biz.consequence.4.d": "Presyo, dates, pickup o kondisyon ng unit ang nagiging pagtatalunan.",
  "biz.consequence.payoff.a": "Hindi ibig sabihin na masama ang staff.",
  "biz.consequence.payoff.b": "Ang problema ay kapag nasa iba't ibang lugar ang importanteng impormasyon.",

  // Section: "One rental. One record."
  "biz.standard.eyebrow": "One rental. One record.",
  "biz.standard.title": "Bawat rental dapat kayang sagutin agad ang limang tanong.",
  "biz.standard.q1": "Sino ang customer?",
  "biz.standard.q2": "Anong unit?",
  "biz.standard.q3": "Kailan?",
  "biz.standard.q4": "Ano ang napagkasunduan: presyo, advance, security deposit, at sino ang magmamaneho?",
  "biz.standard.q5": "Ano ang kailangang asikasuhin next?",
  "biz.standard.note": "Kung kailangan pang mag-search sa Messenger, mag-check ng ibang sheet at magtanong sa ibang tao, kalat pa rin sa iba't ibang lugar ang rental information.",
  "biz.standard.rule": "Rental ba? I-record.",
  "biz.standard.rule.before": "“Sino nakausap niya?”",
  "biz.standard.rule.after": "“Nasa ConfirmPH na?”",

  // Section: "Start the day clear"
  "biz.today.eyebrow": "Start the day clear",
  "biz.today.title": "Alamin kung ano ang booked. Ano ang available. At ano ang kailangang asikasuhin.",
  "biz.today.pickups": "Pickups today",
  "biz.today.returns": "Returns today",
  "biz.today.waiting": "Waiting for confirmation",
  "biz.today.issues": "Vehicle issues",
  "biz.today.followups": "Follow-ups",
  "biz.today.cta": "Buksan ang ConfirmPH. Alamin agad kung ano ang kailangan ng attention.",
  "biz.today.benefit": "Dapat alam ng negosyo mo kung ano ang nangyari kahit hindi ikaw ang kumausap sa customer.",
  "biz.today.owner": "Hindi dapat ikaw ang memory ng buong negosyo.",

  // Section: product walkthrough (component: BusinessProductWalkthrough)
  "biz.proof.eyebrow": "Real product. Demo data.",
  "biz.proof.title": "Tingnan ang isang rental mula Messenger hanggang return.",
  "biz.proof.mode.normal": "Normal na rental",
  "biz.proof.mode.issue": "Paano kung may problema?",
  "biz.proof.cta.normal": "Simulan ang rental walkthrough",
  "biz.proof.cta.issue": "Tingnan kung ano ang nangyayari kapag may problema",
  "biz.proof.browse_all": "Tingnan lahat ng demo scenario",

  // Section: "Maliit pa kami" objection
  "biz.small.eyebrow": "“Maliit pa kami.”",
  "biz.small.title": "Hindi kailangan ng malaking fleet para kailanganin ang malinaw na bookings.",
  "biz.small.body": "May 3 units man o 30, pareho pa rin ang expectation ng customer: tamang unit, tamang araw, tamang booking.",
  "biz.small.body2": "At mas madaling gumawa ng magandang system bago dumami ang bookings, units at staff at maging mahirap nang kontrolin ang lumang paraan.",
  "biz.small.note.a": "Huwag ilipat agad ang buong negosyo.",
  "biz.small.note.b": "Subukan lang ang next 5 real bookings sa ConfirmPH.",
  "biz.small.reassure1": "Tuloy ang Messenger",
  "biz.small.reassure2": "Magsimula sa booking + confirmation",
  "biz.small.reassure3": "Gamitin lang ang ibang features kapag kailangan mo na",

  // Section: onboarding reassurance ("not an IT project")
  "biz.simple.eyebrow": "Hindi kailangan maging techy",
  "biz.simple.title": "Hindi dapat parang IT project ang pag-aayos ng bookings.",
  "biz.simple.flow": "Add booking → Choose unit → Add customer → Send confirmation → Continue the rental",
  "biz.simple.help.t": "Kailangan ng tulong?",
  "biz.simple.help.d": "Puwede naming i-guide ang team mo sa initial setup at first real bookings.",

  // Section: "Customers don't see your system. They feel it."
  "biz.feel.eyebrow": "Customers don't see your system.",
  "biz.feel.title": "Pero nararamdaman nila kung maayos ito.",
  "biz.feel.1": "Nararamdaman nila kapag malinaw ang booking.",
  "biz.feel.2": "Nararamdaman nila kapag pareho ang sagot ng iba't ibang staff.",
  "biz.feel.3": "Nararamdaman nila kapag ready ang tamang unit.",
  "biz.feel.4": "Nararamdaman nila kapag inaasikaso ang problema bago pa sila kailangang mangulit.",
  "biz.feel.5": "Nararamdaman nila kapag hindi nila kailangang ipaliwanag ulit ang parehong booking.",
  "biz.feel.payoff": "Kahit maliit ang rental business, puwedeng professional ang experience.",
  "biz.feel.note": "Panatilihin ang personal na serbisyo. Ayusin ang system sa likod nito.",

  // Section: "Less sisihan. More clarity."
  "biz.clarity.eyebrow": "Less sisihan. More clarity.",
  "biz.clarity.less1": "“Sino nag-confirm nito?”",
  "biz.clarity.less2": "“Akala ko ikaw nag-handle.”",
  "biz.clarity.less3": "“Hindi ko nakita yung message.”",
  "biz.clarity.more": "“Nandito ang booking.”",
  "biz.clarity.1": "Booking recorded",
  "biz.clarity.2": "Unit recorded",
  "biz.clarity.3": "Customer confirmation recorded",
  "biz.clarity.4": "Changes recorded",
  "biz.clarity.5": "Relevant activity recorded",
  "biz.clarity.note": "Pare-pareho na ang information na puwedeng tingnan ng team.",

  // Section: "Before you compare the price"
  "biz.money.eyebrow": "Before you compare the price",
  "biz.money.title": "Libre ang Messenger. Ang mistakes, hindi.",
  "biz.money.1": "Isang forgotten booking",
  "biz.money.2": "Isang preventable double booking",
  "biz.money.3": "Isang customer na kailangang i-refund o hanapan ng ibang unit",
  "biz.money.4": "Isang araw na ginugol para malaman kung ano talaga ang napag-usapan",
  "biz.money.5": "Isang vehicle problem na late nalaman ng next customer",
  "biz.money.note.a": "Hindi ang Messenger message ang mahal.",
  "biz.money.note.b": "Ang mahal ay ang problema kapag walang shared record ang negosyo.",

  // Section: "Protect the bookings you worked hard to get"
  "biz.protect.eyebrow": "Protect the bookings you worked hard to get",
  "biz.protect.title": "Huwag hayaang mawala ang booking pagkatapos mong makuha ang customer.",
  "biz.protect.1": "Nagpo-post ka.",
  "biz.protect.2": "Sumasagot ka sa inquiries.",
  "biz.protect.3": "Nagfo-follow up ka.",
  "biz.protect.4": "Bumili at nagme-maintain ka ng mga unit.",
  "biz.protect.5": "Nagbu-build ka ng reputation.",
  "biz.protect.note": "Kapag nag-yes na ang customer, huwag hayaang mawala ang booking sa pagitan ng messages, tao at memory.",

  // Section: RUN / TRUST / GROW
  "biz.groups.eyebrow": "Lahat ng kaya ng ConfirmPH",
  "biz.groups.run.t": "Run the rental",
  "biz.groups.run.d": "Bookings at agreements, fleet, inspections, maintenance, incidents, staff at records, kasama ang Legal Readiness, company drivers at authorized self-drive drivers, Finance & Books, at return at settlement.",
  "biz.groups.trust.t": "Build customer trust",
  "biz.groups.trust.d": "Confirmation links, booking history, public profile at reviews mula sa actual bookings.",
  "biz.groups.grow.t": "Grow the business",
  "biz.groups.grow.d": "Discover Rentals, public presence, websites, integrations at advanced tools.",
  "biz.groups.cta": "Tingnan lahat ng kayang gawin ng ConfirmPH",

  // Final CTA
  "biz.final.title.a": "Papasok din ang next booking mo.",
  "biz.final.title.b": "Saan mo ito ilalagay?",
  "biz.final.q1": "Sa Messenger thread?",
  "biz.final.q2": "Sa screenshot?",
  "biz.final.q3": "Sa notebook?",
  "biz.final.q4": "Sa memory ng isang tao?",
  "biz.final.q5": "O sa isang lugar na makikita ng buong team?",
  "biz.final.instruction": "Ilagay ang next 5 bookings sa ConfirmPH.",
  "biz.final.keep1": "Tuloy ang Messenger.",
  "biz.final.keep2": "Tuloy ang Facebook.",
  "biz.final.keep3": "Tuloy ang WhatsApp.",
  "biz.final.keep4": "Tuloy ang calls at walk-ins.",
  "biz.final.instruction2": "Bigyan lang ng isang malinaw na record ang next five rentals.",
  "biz.final.cta.primary": "Gamitin ang ConfirmPH sa next 5 bookings ko",
  "biz.final.cta.secondary": "Tingnan muna ang demo",
  "biz.final.microcopy": "30 days free · No card required",
  "biz.final.brandline": "Message kahit saan. Patakbuhin ang rental sa ConfirmPH.",

  // Business navigation (see components/PublicLayout.jsx)
  "biz.nav.product": "Produkto",
  "biz.nav.how_it_works": "Paano Gumagana",
  "biz.nav.plans": "Mga Plano",
  "biz.nav.guides": "Business Guides",
  "biz.nav.help": "Tulong",

  // Customer-first home (`/`): see the matching English block for the
  // section-by-section brief this was built from.
  "home.customer.badge": "Para sa mga renter at customer",

  // 1. Hero
  "home.hero.title.a": "Bago ka magbayad pa o bumiyahe papunta sa pickup, siguraduhing",
  "home.hero.title.b": "talagang nakatala ang booking mo.",
  "home.hero.body1": "Napag-usapan na ninyo ang sasakyan. Na-confirm na ang dates. Baka nag-follow up ka pa ulit.",
  "home.hero.body2.pre": "Pero ang simpleng mensaheng ",
  "home.hero.body2.word": "confirmed",
  "home.hero.body2.post": " ay hindi palaging malinaw kung ano talaga ang na-record ng rental business.",
  "home.hero.lead": "Binibigyan ka ng ConfirmPH ng malinaw na record ng sasakyan, dates, presyo at pickup details bago ka umasa sa booking.",
  "home.hero.cta.primary.helper": "Alamin kung ano ang na-record ng rental business.",
  "home.hero.compat": "Nag-book sa Messenger, Facebook, WhatsApp, Viber, phone o walk-in? Okay lang iyon.",

  // Shared CTAs reused across several sections of this page.
  "home.cta.check_booking": "Suriin ang booking ko",
  "home.cta.get_in_writing": "Ipa-record ang booking ko",
  "home.cta.discover": "Tingnan ang mga rental",
  "home.cta.still_looking": "May hinahanap ka pang rental?",

  // 2. Hero visual: chat vs. structured record
  "home.hero.visual.chat.customer": "Available Toyota Vios June 12 to 15?",
  "home.hero.visual.chat.business": "Yes sir confirmed 👍",
  "home.hero.visual.transition": "Pero ano mismo ang na-confirm?",
  "home.hero.visual.record.badge": "Nakatala ang booking",
  "home.hero.visual.record.vehicle": "Toyota Vios",
  "home.hero.visual.record.dates": "June 12 to 15",
  "home.hero.visual.record.pickup.label": "Pickup",
  "home.hero.visual.record.pickup.value": "Dumaguete Airport",
  "home.hero.visual.record.price.label": "Presyo",
  "home.hero.visual.record.price.value": "₱6,000",
  "home.hero.visual.record.deposit.label": "Deposit",
  "home.hero.visual.record.deposit.value": "₱1,500",
  "home.hero.visual.record.ref.label": "Booking reference",
  "home.hero.visual.record.ref.value": "BK-7K3M9P2X",
  "home.hero.visual.record.confirmed": "Confirmed ng customer",
  "home.hero.visual.payoff.a": "Sinasabi ng chat kung ano ang sinabi sa iyo.",
  "home.hero.visual.payoff.b": "Ipinapakita ng booking record kung ano talaga ang naitala.",

  // 3. The standard: four questions to test a booking against
  "home.standard.eyebrow": "Bago mo sabihing confirmed na",
  "home.standard.title": "Masasagot mo ba ang apat na ito nang hindi naghahalungkat sa messages?",
  "home.standard.q1.t": "Anong sasakyan?",
  "home.standard.q1.d": "Eksaktong sasakyan o malinaw na napagkasunduang vehicle category.",
  "home.standard.q2.t": "Anong dates?",
  "home.standard.q2.d": "Eksaktong pickup at return date at oras.",
  "home.standard.q3.t": "Magkano?",
  "home.standard.q3.d": "Rental price, anumang advance o downpayment, anumang refundable security deposit, at ang natitirang rental balance.",
  "home.standard.q4.t": "Saan ang pickup?",
  "home.standard.q4.d": "Eksaktong pickup location at contact ng rental business.",
  "home.standard.payoff": "Kung may isa rito na hindi malinaw, hindi pa malinaw ang booking.",

  // 4. The consequence: why check before you go
  "home.consequence.eyebrow": "Bakit dapat i-check bago umalis",
  "home.consequence.title": "Mas nagiging magastos ang problema sa booking kapag huli mo na itong nalaman.",
  "home.consequence.1.t": "Bago magbayad",
  "home.consequence.1.d": "May oras ka pang linawin ang sasakyan, dates, presyo, advance o security deposit.",
  "home.consequence.2.t": "Bago bumiyahe",
  "home.consequence.2.d": "May oras ka pang kumpirmahin ang pickup location at contact details.",
  "home.consequence.3.t": "Kapag nasa pickup ka na",
  "home.consequence.3.d": "Ayaw mong doon pa lang malaman na hindi tugma ang booking details.",
  "home.consequence.payoff": "Mas mabuting makita ang problema habang may oras pa para ayusin ito.",

  // 5. The mechanism: how ConfirmPH works
  "home.flow.eyebrow": "Paano gumagana ang ConfirmPH",
  "home.flow.title": "Mag-book gaya ng nakasanayan mo. Magdagdag lang ng isang malinaw na confirmation step.",
  "home.flow.1.t": "Mag-book gaya ng dati",
  "home.flow.1.d": "Messenger, Facebook, WhatsApp, Viber, phone o personal na walk-in.",
  "home.flow.2.t": "Itatala ng rental business ang booking",
  "home.flow.2.d": "Sasakyan, dates, service mode, presyo, advance at security deposit, pickup at customer details.",
  "home.flow.3.t": "Ire-review mo ang parehong details",
  "home.flow.3.d": "I-confirm ang partikular na booking version na ipinakita sa iyo, o ipapa-correct kung may mali.",
  "home.flow.payoff": "Pareho na ninyong makikita at mare-reference ang parehong booking record.",
  "home.flow.note": "Hindi mo kailangang baguhin kung paano ka nagbu-book. Hindi mo lang kailangang umasa sa chat lamang.",

  // 6. Product proof: the interactive demo (component: ProductWalkthrough)
  "home.proof.eyebrow": "Tingnan mismo",
  "home.proof.title": "Tingnan kung ano mismo ang nakikita ng renter.",
  "home.proof.subtitle": "Sundan ang isang demo rental mula booking details at confirmation hanggang pickup at completion.",
  "home.proof.mode.normal": "Normal na rental",
  "home.proof.mode.issue": "Paano kung may problema?",
  "home.proof.teaser.normal": "Sundan ang tunay na renter journey: mula sa paghahanap ng rental hanggang sa pag-iwan ng review. Totoong mga screen, walang aalis sa page na ito.",
  "home.proof.teaser.issue": "Tingnan kung paano hinahawakan ng ConfirmPH ang problema sa sasakyan ng isang naka-confirm na booking: mula sa babala hanggang sa muling pagkumpirma mo. Totoong mga screen, walang aalis sa page na ito.",
  "home.proof.cta.normal": "Tingnan ang isang confirmed booking",
  "home.proof.cta.issue": "Tingnan kung paano tumutugon ang ConfirmPH",
  "home.proof.business_prompt": "May negosyo kang rental?",
  "home.proof.business_link": "Tingnan kung paano gumagana ang ConfirmPH para sa negosyo mo",

  // 7. Trust through limitation
  "home.trust.title": "Ano talaga ang ibig sabihin ng \"confirmed\"?",
  "home.trust.does.t": "Ano ang kino-confirm ng ConfirmPH",
  "home.trust.does.d": "Na tinanggap ng customer ang partikular na booking version at material terms na ipinakita noong oras na iyon. Kung may material term na magbago pagkatapos, kailangan ng sariling acceptance ang bagong version; hindi nito mamanahin ang naunang confirmation.",
  "home.trust.doesnot.t": "Ano ang hindi ginagarantiya ng ConfirmPH",
  "home.trust.doesnot.d": "Hindi nito ginagarantiya na walang maaaring magbago, na palaging magiging perpekto ang serbisyo ng rental business, o na pagmamay-ari o kontrolado ng ConfirmPH ang rental business.",
  "home.trust.footer": "Binibigyan ng ConfirmPH ang parehong panig ng mas malinaw na booking record. Ang rental business pa rin ang responsable sa mismong rental.",

  // 8. The habit, before pickup
  "home.habit.eyebrow": "Bago ang pickup",
  "home.habit.title": "Anim na bagay na dapat alam mo nang hindi na binubuksan ang Messenger.",
  "home.habit.1": "Anong sasakyan",
  "home.habit.2": "Eksaktong pickup at return dates",
  "home.habit.3": "Napagkasunduang presyo, anumang advance, at ang security deposit",
  "home.habit.4": "Eksaktong pickup location",
  "home.habit.5": "Sino ang tatawagan kung may magbago",
  "home.habit.6": "Sino ang magmamaneho: self-drive na may authorized driver, o driver mula sa business",
  "home.habit.mantra": "Bago ang pickup, i-check ang ConfirmPH.",

  // 9. Still looking: discovery folded into the same journey
  "home.discover.eyebrow": "May hinahanap ka pang rental?",
  "home.discover.title": "Magsimula sa mga rental business na gumagamit ng ConfirmPH.",
  "home.discover.subtitle": "Tingnan ang mga participating rental business, kanilang listed vehicles at public information, pagkatapos ay direktang makipag-ugnayan sa business.",
  "home.discover.journey": "Maghanap → Mag-book → I-record → I-confirm → Readiness → Handover",

  // 10. Final CTA
  "home.final.title.a": "Na-check mo na ang messages.",
  "home.final.title.b": "Ngayon, i-check mo naman ang booking.",
  "home.final.body": "Bago ka magbayad pa o bumiyahe papunta sa pickup, siguraduhing malinaw na nakatala ang sasakyan, dates, presyo at pickup details.",
  "home.final.secondary.label": "Nag-book sa labas ng ConfirmPH?",

  "home.business.bridge.eyebrow": "May negosyong rental?",
  "home.business.bridge.title": "Huwag nang mawala ang booking dahil sa nakalimutang mensahe.",
  "home.business.bridge.subtitle": "Binibigyan ng ConfirmPH ang mga maliliit na rental sa Pilipinas ng isang panatag na dashboard para kumpirmahin ang booking, mahuli ang dobleng booking, at makapag-follow up sa tamang oras.",
  "home.business.bridge.cta": "Alamin para sa negosyo",

  // Dashboard workspace
  "dash.title": "Trabaho ngayong araw",
  "dash.subtitle": "Ano ang dapat gawin ngayon.",
  "dash.how_it_works": "Paano gumagana ang ConfirmPH",
  "dash.attention.title": "Ano ang kailangan ng pansin?",
  "dash.attention.subtitle": "Pinakamahalagang asikasuhin ngayon",
  "dash.attention.empty": "Walang madaliang gawain ngayon. Magdagdag ng booking o magpahinga muna: kayo ang bahala.",
  "dash.checklist.title": "Suriin ngayon",
  "dash.checklist.subtitle": "5-minutong checklist",
  "dash.chk.pending": "Kumpirmahin ang mga bagong booking",
  "dash.chk.today": "I-check ulit ang mga sasakyan ngayon",
  "dash.chk.problems": "Ayusin ang mga problema sa sasakyan",
  "dash.chk.followups": "Asikasuhin ang mga follow-up",
  "dash.chk.customer_requests": "Sagutin ang mga hiling ng kustomer",
  "dash.ws.today_pickups": "Pickup Ngayong Araw",
  "dash.ws.today_returns": "Return Ngayong Araw",
  "dash.ws.needs_confirmation": "Hinihintay na Kumpirmahin",
  "dash.ws.problems": "Mga Problema",
  "dash.ws.followups": "Follow-up sa Ngayon",
  "dash.ws.customer_requests": "Hiling ng Kustomer",
  "dash.upcoming.title": "Mga paparating na booking",
  "dash.upcoming.subtitle": "Susunod na 7 araw",
  "dash.upcoming.empty": "Walang paparating ngayong linggo.",
  "dash.at_risk.title": "Mga booking na delikado",
  "dash.at_risk.subtitle": "Magkakapatong na booking at mga babala",
  "dash.at_risk.empty": "Walang nakitang conflict. Mabuti naman.",

  // Booking status labels
  "status.pending_client_confirmation": "Hinihintay Kumpirmahin",
  "status.client_confirmed": "Kinumpirma ng Kliyente",
  "status.change_requested": "Hiniling na Baguhin",
  "status.at_risk": "Delikado",
  "status.affected_action_needed": "Kailangan ng Aksyon",
  "status.cancelled": "Kanselado",
  "status.completed": "Tapos na",

  // Status tooltips
  "tip.awaiting_staff_approval": "Kailangan ng pagsusuri ng staff bago magpatuloy.",
  "tip.pending_client_confirmation": "Hindi pa nakukumpirma ng kustomer ang detalye ng booking.",
  "tip.reconfirmation_required": "Nagbago ang detalye ng booking pagkatapos kumpirmahin ng kustomer: kailangan nilang muling kumpirmahin.",
  "tip.client_confirmed": "Sinuri at kinumpirma na ng kustomer ang detalye.",
  "tip.change_requested": "May gusto pang baguhin ang kustomer.",
  "tip.ongoing": "Nakuha na ang sasakyan: kasalukuyang nangyayari ang rental.",
  "tip.at_risk": "May maaaring makaapekto sa booking na ito. Suriin bago ang pickup.",
  "tip.affected_action_needed": "Maaaring apektado ang booking na ito ng isang problema.",
  "tip.completed": "Tapos na ang booking.",
  "tip.cancelled": "Kinansela ang booking na ito.",
  "tip.no_show": "Hindi dumating ang customer para sa pickup.",

  // Empty states
  "empty.bookings.title": "Wala pang booking",
  "empty.bookings.body": "Kapag may nag-message, tumawag, o pumuntang kustomer, idagdag dito ang booking para makumpirma nila ito.",
  "empty.vehicles.title": "Wala pang sasakyan",
  "empty.vehicles.body": "Idagdag muna ang mga sasakyan ninyo para maikonekta ang booking sa tamang kotse, motor, van, o SUV.",
  "empty.problems.title": "Walang aktibong problema sa sasakyan",
  "empty.problems.body": "Kung may sira, late, o hindi pansamantalang magagamit ang sasakyan, itala ito dito para mabigyan ng babala ang mga apektadong kustomer.",
  "empty.followups.title": "Walang follow-up sa ngayon",
  "empty.followups.body": "Ipapakita ng ConfirmPH ang mga follow-up dito kapag may booking na kailangang asikasuhin: mga nakabinbing kumpirmasyon, pre-pickup check, at return prep.",
  "empty.customer_requests.title": "Wala pang hiling ng kustomer",
  "empty.customer_requests.body": "Lalabas ang mga ito kapag may kustomer na humihingi ng kumpirmasyon sa inyong negosyo sa pamamagitan ng ConfirmPH.",

  // Find My Booking
  "find.title": "Hanapin ang booking ko",
  "find.subtitle": "Tingnan kung naitala ang inyong booking sa ConfirmPH system ng kompanya.",
  "find.noresult.title": "Walang nahanap na booking sa ConfirmPH.",
  "find.noresult.body": "Hindi ito agad nangangahulugang kansela ang inyong booking. Maaaring hindi pa ito naitala ng negosyo, o hindi sila gumagamit ng ConfirmPH. Makipag-ugnayan sa kanila o humingi ng kumpirmasyon para maiwasan ang sorpresa.",
  "find.cta.request": "Humiling ng Kumpirmasyon ng Booking",

  // Settings
  "settings.title": "Mga Setting",
  "settings.language.title": "Wika",
  "settings.language.subtitle": "Piliin kung paano makikipag-usap sa inyo ang ConfirmPH. Ang setting na ito ay sa device na ito lang.",

  // Warning Center (nav)
  "nav.warnings": "Mga Babala",

  // Warning Center: page
  "warnings.page.title": "Warning Center",
  "warnings.page.subtitle": "Mga operational risk na natukoy namin sa inyong mga booking, sasakyan, at insidente. Suriin ang bawat item at kumilos: walang awtomatikong ipinapadala sa inyong mga customer.",
  "warnings.rescan": "I-refresh ang mga babala",
  "warnings.loading": "Nilo-load ang mga babala…",
  "warnings.tab.active": "Aktibo",
  "warnings.tab.closed": "Sarado",
  "warnings.filter.all_types": "Lahat ng uri",
  "warnings.filter.all_severities": "Lahat ng antas",
  "warnings.severity.critical": "Kritikal",
  "warnings.severity.high": "Mataas",
  "warnings.severity.medium": "Katamtaman",
  "warnings.severity.low": "Mababa",
  "warnings.severity.info": "Impormasyon",
  "warnings.empty.active.title": "Malinis: walang aktibong babala sa ngayon.",
  "warnings.empty.active.body": "Ipapakita namin dito ang anumang kailangan ninyong pansinin.",
  "warnings.empty.closed.title": "Wala pang ipapakita dito.",
  "warnings.empty.closed.body": "Lalabas dito ang mga sarado nang babala.",
  "warnings.toast.scan_complete": "Tapos na ang scan: {detected} isyu ang natukoy, {fixed} awtomatikong naayos.",

  // Warning Center: card
  "warnings.recommended": "Inirerekomenda",
  "warnings.detected_at": "Natukoy noong",
  "warnings.last_seen": "huling nakita",
  "warnings.closed_at": "sarado noong",
  "warnings.internal_notes": "Mga internal na tala",
  "warnings.show_notes": "Mga tala",
  "warnings.hide_notes": "Itago",
  "warnings.more_actions": "Iba pang aksyon",
  "warnings.affected.booking": "Booking",
  "warnings.affected.vehicle": "Sasakyan",
  "warnings.affected.incident": "Insidente",

  // Warning Center: status labels
  "warnings.status.active": "Aktibo",
  "warnings.status.reviewed": "Nasuri na",
  "warnings.status.closed": "Sarado",
  "warnings.status.fixed": "Awtomatikong naayos",

  // Warning Center: actions
  "warnings.action.reassign": "Palitan ang sasakyan",
  "warnings.action.notify": "Abisuhan ang customer",
  "warnings.action.open_booking": "Buksan ang booking",
  "warnings.action.open_incident": "Buksan ang insidente",
  "warnings.action.open_vehicle_documents": "Buksan ang mga dokumento ng sasakyan",
  "warnings.action.open_vehicle_maintenance": "Tingnan ang maintenance",
  "warnings.action.review": "Markahang nasuri na",
  "warnings.action.add_note": "Magdagdag ng tala",
  "warnings.action.close": "Isara",

  // Warning Center: notify / note forms
  "warnings.notify_placeholder": "Custom na mensahe (opsyonal: magpapadala kami ng ligtas na default kung iiwan itong blangko)",
  "warnings.notify.disclaimer": "Hindi kami awtomatikong nag-aabiso sa mga customer: ipapadala lang ito kapag pinindot ninyo ang Send.",
  "warnings.send_notification": "Ipadala ang abiso",
  "warnings.note_placeholder": "Internal na tala (ang inyong team lang ang makakakita)",
  "warnings.save_note": "I-save ang tala",
  "warnings.toast.reviewed": "Namarkahang nasuri na",
  "warnings.toast.closed": "Sarado na ang babala",
  "warnings.toast.note_empty": "Hindi maaaring blangko ang tala.",
  "warnings.toast.note_added": "Naidagdag ang tala",
  "warnings.toast.customer_notified": "Naabisuhan na ang customer",

  // Warning Center: close (structured reasons)
  "warnings.close.title": "Bakit ninyo isasara ang babalang ito?",
  "warnings.close.reason.resolved": "Naayos na",
  "warnings.close.reason.not_an_issue": "Hindi isyu",
  "warnings.close.reason.accepted_risk": "Tinanggap ang risk",
  "warnings.close.reason.duplicate": "Duplicate",
  "warnings.close.reason.other": "Iba pa",
  "warnings.close.note_required_placeholder": "Ipaliwanag nang maikli kung bakit (kailangan)",
  "warnings.close.note_optional_placeholder": "Magdagdag ng tala (opsyonal)",
  "warnings.close.note_required": "Kailangan ng maikling tala para sa dahilang ito.",
  "warnings.close.submit": "Isara ang babala",

  // Warning Center: banner
  "warnings.banner.count_one": "1 aktibong babala",
  "warnings.banner.count_many": "{count} aktibong babala",
  "warnings.banner.minimize": "I-minimize",
  "warnings.banner.expand": "Palawakin",

  // Warning Center: types (chip label)
  "warnings.type.booking_overlap": "Magkakapatong na booking",
  "warnings.type.buffer_violation": "Maikling agwat",
  "warnings.type.vehicle_unavailable": "Hindi available ang sasakyan",
  "warnings.type.incident_impact": "Isyu sa sasakyan",
  "warnings.type.missing_fields": "Kulang na detalye",
  "warnings.type.pending_too_long": "Naghihintay ng kumpirmasyon",
  "warnings.type.return_overdue": "Huli nang pagbalik",
  "warnings.type.deposit_unresolved": "Hindi pa nareresolba ang deposit",
  "warnings.type.missing_return_inspection": "Kulang ang inspection",
  "warnings.type.inspection_damage": "Posibleng sira",
  "warnings.type.vehicle_document_expiry": "Malapit nang mag-expire ang dokumento",
  "warnings.type.location_risk": "Panganib sa lokasyon / distansya",
  "warnings.type.maintenance_due": "May paparating na maintenance",

  // Warning Center: "why it matters"
  "warnings.why.booking_overlap": "May dalawang customer na naka-book sa parehong sasakyan sa magkakapatong na oras: wawalan ng sasakyan ang isa kapag hindi ito naayos bago mag-pickup.",
  "warnings.why.buffer_violation": "Maaaring hindi na sapat ang oras para linisin, siyasatin, at ihanda ang sasakyan bago dumating ang susunod na customer.",
  "warnings.why.vehicle_unavailable": "Hindi pa handang i-rent ang sasakyang naka-assign sa booking na ito: baka walang sasakyang matagpuan ang customer.",
  "warnings.why.incident_impact": "Ang bukas na insidente sa sasakyang ito ay maaaring makaapekto sa pickup ng customer o sa kalagayan ng sasakyan.",
  "warnings.why.missing_fields": "Kung kulang ang contact o scheduling details, maaaring hindi maabot ng staff ang customer o husto ang paghahanda sa booking.",
  "warnings.why.pending_too_long": "Wala pang kumpirmasyon ang customer pagkalipas ng 24+ oras: baka nakalaan ang sasakyan sa booking na hindi na matutuloy.",
  "warnings.why.return_overdue": "Hindi pa naibabalik ang sasakyan sa takdang oras, na maaaring maantala o mapigilan ang susunod na booking sa sasakyang ito.",
  "warnings.why.deposit_unresolved": "Tapos na ang rental mahigit isang linggo na ang nakalipas ngunit hindi pa naibabalik o naitatago ang deposit: maaari itong maging dispute sa bandang huli.",
  "warnings.why.missing_return_inspection": "Kung walang naitalang return inspection, mahirap resolbahin ang mga dispute tungkol sa sira o deposit sa bandang huli.",
  "warnings.why.inspection_damage": "Ang mga hindi pa nasusuring natukoy na sira ay maaaring maantala ang desisyon sa deposit at anumang bayarin sa pagkukumpuni.",
  "warnings.why.vehicle_document_expiry": "Ang expired o malapit nang mag-expire na dokumento ay maaaring maging dahilan para hindi ito maituring na compliant kapag ni-rent out.",
  "warnings.why.location_risk": "Maaaring makaapekto sa booking na ito ang distansya ng pickup/return: kumpirmahin ang logistics sa customer.",
  "warnings.why.maintenance_due": "Ang pag-antala sa naka-iskedyul na serbisyo ay maaaring humantong sa breakdown, isyu sa kaligtasan, o mas malaking gastos sa pagkukumpuni sa bandang huli.",
};

// ---------------------------------------------------------------------------
export const TRANSLATIONS = { en, tl };
